import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Copy, FileStack, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { isSpecies } from '@zt/content';
import { Button, Dialog, Field, Input, Select } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ProductCopy, ProductDetail, ProductTemplate, SkuSuggestion } from '../lib/types';
import type { ProductForm } from './products';

/**
 * Ürün araçları (F5B-07): kopyalama, şablon (kaydet / şablondan başla / yönet), SKU önerisi ve SKU kalıbı.
 */

/** Ürün ekranının üst düğmeleri: Kopyala ve Şablon olarak kaydet. */
export function ProductToolsActions({ product }: { product: ProductDetail }) {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [templateOpen, setTemplateOpen] = useState(false);
  const copy = useApiMutation(() => api.post<ProductCopy>(`/v1/admin/products/${product.id}/copy`), {
    invalidate: [['products']],
    onSuccess: (r) => {
      toast.success(t('admin.productTools.copied', { skus: r.skus.join(', ') }));
      void navigate({ to: '/urunler/$id', params: { id: r.id } });
    },
  });
  if (!can('catalog.product.write')) return null;
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <ConfirmButton
          variant="secondary"
          message={t('admin.productTools.copyConfirm')}
          size="md"
          onConfirm={() => copy.mutateAsync(undefined)}
        >
          <Copy className="h-4 w-4" aria-hidden />
          {t('admin.productTools.copy')}
        </ConfirmButton>
        <HelpTip k="products.copy" />
      </span>
      <span className="inline-flex items-center gap-1">
        <Button variant="secondary" onClick={() => setTemplateOpen(true)}>
          <FileStack className="h-4 w-4" aria-hidden />
          {t('admin.productTools.saveTemplate')}
        </Button>
        <HelpTip k="products.save_template" />
      </span>
      {templateOpen && <SaveTemplateDialog product={product} onClose={() => setTemplateOpen(false)} />}
    </>
  );
}

function SaveTemplateDialog({ product, onClose }: { product: ProductDetail; onClose: () => void }) {
  const t = useT();
  const [name, setName] = useState(product.title);
  const save = useApiMutation(
    () => api.post<ProductTemplate>(`/v1/admin/products/${product.id}/template`, { name }),
    {
      invalidate: [['product-templates']],
      success: t('admin.productTools.templateSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.productTools.saveTemplate')}
      description={t('admin.productTools.saveTemplateLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('admin.productTools.templateName')} required error={fieldError(save.error, 'name')}>
        {(p) => <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}
      </Field>
    </Dialog>
  );
}

/** Şablon verisini ürün formuna uygular (yalnızca şablonda dolu olan alanlar). */
export function templatePatch(data: ProductTemplate['data']): Partial<ProductForm> {
  const d = data as Record<string, unknown>;
  const patch: Partial<ProductForm> = {};
  const str = (k: keyof ProductForm) => {
    if (typeof d[k] === 'string') (patch as Record<string, unknown>)[k] = d[k];
  };
  if (typeof d.brandId === 'string') patch.brandId = d.brandId;
  if (Array.isArray(d.species)) patch.species = (d.species as string[]).filter(isSpecies);
  if (Array.isArray(d.categoryIds)) patch.categoryIds = d.categoryIds as string[];
  if (typeof d.taxClassId === 'string') patch.taxClassId = d.taxClassId;
  for (const k of ['b2cVisible', 'b2bVisible', 'healthContent'] as const)
    if (typeof d[k] === 'boolean') patch[k] = d[k] as boolean;
  for (const k of ['shortDescription', 'description', 'composition', 'usage', 'warnings'] as const) str(k);
  if (Array.isArray(d.tags)) patch.tags = (d.tags as string[]).join(', ');
  if (Array.isArray(d.options))
    patch.options = (d.options as Array<{ name: string; values: string[] }>).map((o) => ({
      name: o.name,
      values: o.values.join(', '),
    }));
  if (Array.isArray(d.attributes))
    patch.attributes = Object.fromEntries(
      (d.attributes as Array<{ attributeId: string; value: string | string[] }>).map((a) => [
        a.attributeId,
        Array.isArray(a.value) ? a.value : [a.value],
      ]),
    );
  return patch;
}

/** Yeni ürün ekranı: şablondan başla ve şablonları yönet. */
export function TemplatePicker({ onApply }: { onApply: (patch: Partial<ProductForm>) => void }) {
  const t = useT();
  const { can } = usePermissions();
  const [id, setId] = useState('');
  const list = useQuery({
    queryKey: ['product-templates'],
    queryFn: () => api.get<ProductTemplate[]>('/v1/admin/product-templates'),
  });
  const del = useApiMutation((tid: string) => api.del(`/v1/admin/product-templates/${tid}`), {
    invalidate: [['product-templates']],
    success: t('admin.productTools.templateDeleted'),
    onSuccess: () => setId(''),
  });
  const rows = list.data ?? [];
  if (!rows.length) return <p className="text-sm text-ink-3">{t('admin.productTools.noTemplates')}</p>;
  const chosen = rows.find((r) => r.id === id);
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field
        label={t('admin.productTools.template')}
        help={<HelpTip k="products.template" />}
        className="w-72"
      >
        {(p) => (
          <Select {...p} value={id} onChange={(e) => setId(e.target.value)}>
            <option value="">{t('admin.productTools.chooseTemplate')}</option>
            {rows.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Button
        disabled={!chosen}
        onClick={() => {
          if (!chosen) return;
          onApply(templatePatch(chosen.data));
          toast.success(t('admin.productTools.templateApplied', { name: chosen.name }));
        }}
      >
        {t('admin.productTools.applyTemplate')}
      </Button>
      {chosen && can('catalog.product.write') && (
        <ConfirmButton
          variant="ghost"
          message={t('admin.productTools.deleteTemplateConfirm', { name: chosen.name })}
          onConfirm={() => del.mutateAsync(chosen.id)}
        >
          {t('admin.productTools.deleteTemplate')}
        </ConfirmButton>
      )}
    </div>
  );
}

/** Boş SKU'ları işletmenin SKU kalıbına göre doldurur; kalıp düzenlenebilir. */
export function SkuSuggestButton({
  f,
  productId,
  onApply,
}: {
  f: ProductForm;
  productId: string | null;
  onApply: (skus: string[]) => void;
}) {
  const t = useT();
  const { can } = usePermissions();
  const [patternOpen, setPatternOpen] = useState(false);
  const optionNames = f.options.map((o) => o.name.trim()).filter(Boolean);
  const suggest = useApiMutation(
    () =>
      api.post<SkuSuggestion>('/v1/admin/products/sku-suggestions', {
        title: f.title.trim() || '-',
        brandId: f.brandId || null,
        categoryId: f.categoryIds[0] ?? null,
        variants: f.variants.map((v) => optionNames.map((n) => v.optionValues[n] ?? '').filter(Boolean)),
        ...(productId ? { productId } : {}),
      }),
    {
      onSuccess: (r) => {
        onApply(r.skus);
        toast.success(t('admin.productTools.skusSuggested', { pattern: r.pattern }));
      },
    },
  );
  const empty = f.variants.filter((v) => !v.sku.trim()).length;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Button
        size="sm"
        disabled={!empty || !f.title.trim()}
        loading={suggest.isPending}
        onClick={() => suggest.mutate(undefined)}
      >
        <Wand2 className="h-4 w-4" aria-hidden />
        {t('admin.productTools.suggestSkus')}
      </Button>
      <HelpTip k="products.sku_suggest" />
      {can('org.settings.update') && (
        <Button size="sm" variant="ghost" onClick={() => setPatternOpen(true)}>
          {t('admin.productTools.skuPattern')}
        </Button>
      )}
      {patternOpen && <SkuPatternDialog onClose={() => setPatternOpen(false)} />}
    </span>
  );
}

function SkuPatternDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const q = useQuery({
    queryKey: ['settings', 'catalog'],
    queryFn: () => api.get<{ skuPattern: string }>('/v1/admin/settings/catalog'),
  });
  const [pattern, setPattern] = useState<string | null>(null);
  const value = pattern ?? q.data?.skuPattern ?? '';
  const save = useApiMutation(() => api.put('/v1/admin/settings/catalog', { skuPattern: value }), {
    invalidate: [['settings', 'catalog']],
    success: t('admin.productTools.skuPatternSaved'),
    onSuccess: onClose,
    silentFieldErrors: true,
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.productTools.skuPattern')}
      description={t('admin.productTools.skuPatternLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.productTools.skuPatternLabel')}
        required
        help={<HelpTip k="products.sku_pattern" />}
        description={t('admin.productTools.skuPatternHint')}
        error={fieldError(save.error, 'skuPattern')}
      >
        {(p) => (
          <Input
            {...p}
            value={value}
            maxLength={60}
            className="font-mono"
            onChange={(e) => setPattern(e.target.value)}
          />
        )}
      </Field>
    </Dialog>
  );
}
